<template>
  <el-config-provider :locale="zhCn">
    <div class="min-h-screen bg-gray-100">
      <!-- 已登录状态且不在登录页面 -->
      <template v-if="isAuthenticated && !isLoginPage">
        <div class="flex flex-col h-screen">
          <!-- 顶部导航栏 -->
          <header class="bg-white shadow-sm">
            <div class="flex items-center justify-between h-16 px-6">
              <h1 class="text-xl font-bold text-gray-800">文档管理系统</h1>

              <!-- 用户菜单 -->
              <el-dropdown @command="handleCommand" trigger="click">
                <div class="flex items-center cursor-pointer text-gray-700 hover:text-gray-900">
                  <el-icon class="mr-1"><UserFilled /></el-icon>
                  <span class="text-sm">管理员</span>
                  <el-icon class="ml-1"><ArrowDown /></el-icon>
                </div>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="changePassword" class="!px-4 !py-2">
                      <div class="flex items-center text-gray-600">
                        <el-icon class="mr-2"><Lock /></el-icon>
                        <span>修改密码</span>
                      </div>
                    </el-dropdown-item>
                    <el-dropdown-item command="logout" class="!px-4 !py-2">
                      <div class="flex items-center text-red-600">
                        <el-icon class="mr-2"><SwitchButton /></el-icon>
                        <span>退出登录</span>
                      </div>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </header>

          <!-- 主要内容区域 -->
          <main class="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-6">
            <router-view v-slot="{ Component }">
              <transition
                enter-active-class="transform transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transform transition-all duration-300 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2"
              >
                <component :is="Component" />
              </transition>
            </router-view>
          </main>
        </div>
      </template>

      <!-- 未登录或登录页面 -->
      <template v-else>
        <router-view />
      </template>

      <!-- 添加修改密码弹框 -->
      <el-dialog
        v-model="passwordDialogVisible"
        title="修改密码"
        width="400px"
        :close-on-click-modal="false"
        destroy-on-close
      >
        <el-form ref="passwordFormRef" :model="passwordForm" :rules="passwordRules" label-width="100px" @submit.prevent>
          <el-form-item label="原密码" prop="oldPassword">
            <el-input v-model="passwordForm.oldPassword" type="password" show-password placeholder="请输入原密码" />
          </el-form-item>
          <el-form-item label="新密码" prop="newPassword">
            <el-input v-model="passwordForm.newPassword" type="password" show-password placeholder="请输入新密码" />
          </el-form-item>
          <el-form-item label="确认新密码" prop="confirmPassword">
            <el-input
              v-model="passwordForm.confirmPassword"
              type="password"
              show-password
              placeholder="请再次输入新密码"
            />
          </el-form-item>
        </el-form>
        <template #footer>
          <div class="flex justify-end space-x-2">
            <el-button @click="passwordDialogVisible = false">取消</el-button>
            <el-button type="primary" :loading="changingPassword" @click="handleChangePassword"> 确定 </el-button>
          </div>
        </template>
      </el-dialog>
    </div>
  </el-config-provider>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowDown, UserFilled, SwitchButton, Lock } from '@element-plus/icons-vue'
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import { authApi } from '@/api'

const router = useRouter()
const route = useRoute()

const isAuthenticated = computed(() => {
  return localStorage.getItem('token')
})

const isLoginPage = computed(() => {
  return route.path === '/login'
})

// 修改密码相关
const passwordDialogVisible = ref(false)
const changingPassword = ref(false)
const passwordFormRef = ref(null)
const passwordForm = ref({
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
})

// 密码验证规则
const passwordRules = {
  oldPassword: [
    { required: true, message: '请输入原密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度应在 6 到 20 个字符之间', trigger: 'blur' },
  ],
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度应在 6 到 20 个字符之间', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value === passwordForm.value.oldPassword) {
          callback(new Error('新密码不能与原密码相同'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
  confirmPassword: [
    { required: true, message: '请再次输入新密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value !== passwordForm.value.newPassword) {
          callback(new Error('两次输入的密码不一致'))
        } else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
}

// 统一处理下拉菜单命令
const handleCommand = async (command) => {
  if (command === 'logout') {
    try {
      await authApi.logout() // 调用登出 API
      localStorage.removeItem('token')
      router.push('/login')
      ElMessage.success('退出登录成功')
    } catch (error) {
      console.error('退出登录失败:', error)
      ElMessage.error('退出登录失败')
    }
  } else if (command === 'changePassword') {
    passwordDialogVisible.value = true
  }
}

// 处理修改密码
const handleChangePassword = async () => {
  if (!passwordFormRef.value) return

  try {
    // 表单验证
    await passwordFormRef.value.validate()

    changingPassword.value = true
    await authApi.changePassword({
      oldPassword: passwordForm.value.oldPassword,
      newPassword: passwordForm.value.newPassword,
    })

    ElMessage.success('密码修改成功，请重新登录')
    passwordDialogVisible.value = false
    localStorage.removeItem('token')
    router.push('/login')
  } catch (error) {
    const message = error.response?.data?.message || '修改密码失败'
    ElMessage.error(message)
  } finally {
    changingPassword.value = false
  }
}
</script>

<style>
/* 自定义滚动条 */
::-webkit-scrollbar {
  @apply w-1.5 h-1.5;
}

::-webkit-scrollbar-track {
  @apply bg-transparent;
}

::-webkit-scrollbar-thumb {
  @apply bg-gray-300 rounded-full;
}

::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-400;
}

/* 页面过渡动画 */
.fade-enter-active,
.fade-leave-active {
  @apply transition-all duration-300 ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  @apply opacity-0;
}

/* Element Plus 组件样式优化 */
.el-dropdown-menu__item:not(.is-disabled):hover {
  @apply bg-gray-50;
}

/* 基础样式 */
html,
body,
#app {
  height: 100%;
  margin: 0;
  padding: 0;
}
</style>
